import type {
  ArchivoBancoBNResultado,
  ArchivoBancoGuardado,
  ArchivoBancoResultado,
  ArchivoContado,
  ArchivoPagoContado,
  BankAccountBC,
  Beneficiario,
  Categoria,
  ColaboradorBusqueda,
  Concepto,
  ConceptoSinVincular,
  DetalleSemanaCategoria,
  DiarioPagosBC,
  EliminarConceptosResultado,
  EnvioBCResultado,
  EnvioBCVistaPrevia,
  FiltrosPagosCredito,
  HistoricoPagosCredito,
  ListaContado,
  ListaContadoDetalle,
  Obligacion,
  OrdenCompraBC,
  OrdenCompraDetalleBC,
  PropuestaPagoDetalle,
  PropuestaPagoResumen,
  Proveedor,
  ProveedoresPactadosResultado,
  RefrescoPropuestaResultado,
  ReportePropuesta,
  ReporteProyeccion,
  RespuestaApi,
  ResultadoGenerarContado,
  ResultadoImportacionBC,
  ResumenSemana,
  Semana,
  SincronizacionCreditoResultado,
  SolicitudContado,
  UltimoEnvioBancoPorCuenta,
  UsuarioActual,
  ValidacionArchivoBanco,
  VencimientoDetalle,
  VendorBC,
  VendorDetalleBC,
  VistaPagoContado,
} from '@adelante/shared';
import type {
  ActualizarConceptoSchema,
  ActualizarDetalleCasoSchema,
  ActualizarObligacionSchema,
  ActualizarPagoSchema,
  ActualizarPropuestaPagoSchema,
  ActualizarProveedorSchema,
  ActualizarSaldosSchema,
  ActualizarSolicitudContadoSchema,
  CrearBeneficiarioSchema,
  CrearConceptoSchema,
  CrearCuentaBeneficiarioSchema,
  CrearDetalleCasoSchema,
  CrearObligacionSchema,
  CrearPagoAdicionalSchema,
  CrearPagoSchema,
  CrearPropuestaPagoSchema,
  CrearProveedorSchema,
  CrearSemanaSchema,
  CrearSolicitudContadoSchema,
  EliminarConceptosSchema,
  GenerarArchivoContadoSchema,
  PagarContadoAManoSchema,
  PagoDetalleCasoSchema,
  VincularProveedorConceptoSchema,
} from '@adelante/shared';
import type { z } from 'zod';
import { modoDev, msalInstance, obtenerIniciales, scopesApi, usuarioDev } from './auth';

const API_URL = import.meta.env.VITE_API_URL;

/**
 * Obtiene un access token para llamar a la API Adelante.
 * - En modo dev: devuelve `null` (el backend skipea validación con AUTH_DEV_MODE=true).
 * - En modo real: pide token silencioso a MSAL para el scope custom de la API.
 */
async function obtenerToken(): Promise<string | null> {
  if (modoDev) return null;

  if (!msalInstance) {
    throw new Error('MSAL no está inicializado');
  }

  const cuentas = msalInstance.getAllAccounts();
  const cuenta = cuentas[0];
  if (!cuenta) {
    throw new Error('Sin sesión activa');
  }

  const respuesta = await msalInstance.acquireTokenSilent({
    scopes: scopesApi,
    account: cuenta,
  });

  return respuesta.accessToken;
}

/**
 * Wrapper genérico sobre `fetch` que añade auth, content-type y maneja errores
 * HTTP a alto nivel. Las funciones del cliente lo usan internamente.
 */
/** Error devuelto por la API (`{ error: { codigo, mensaje } }`), con su mensaje legible para el usuario. */
export class ErrorApiCliente extends Error {
  readonly status: number;
  readonly codigo: string | null;
  constructor(mensaje: string, status: number, codigo: string | null) {
    super(mensaje);
    this.name = 'ErrorApiCliente';
    this.status = status;
    this.codigo = codigo;
  }
}

export async function pedir<T>(ruta: string, opciones?: RequestInit): Promise<T> {
  const token = await obtenerToken();

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...((opciones?.headers ?? {}) as Record<string, string>),
  };
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const respuesta = await fetch(`${API_URL}${ruta}`, { ...opciones, headers });

  if (!respuesta.ok) {
    // La API responde `{ data: null, error: { codigo, mensaje } }`: si viene, ese mensaje es el
    // que se le muestra al usuario (ej. el motivo por el que BC rechazó un envío).
    let cuerpo: { error?: { codigo?: string; mensaje?: string } } | null = null;
    try {
      cuerpo = await respuesta.json();
    } catch {
      /* respuesta sin JSON */
    }
    const mensaje = cuerpo?.error?.mensaje;
    if (mensaje)
      throw new ErrorApiCliente(mensaje, respuesta.status, cuerpo?.error?.codigo ?? null);
    throw new Error(`HTTP ${respuesta.status}: ${respuesta.statusText}`);
  }

  return (await respuesta.json()) as T;
}

/** Igual que `pedir`, pero para descargar un archivo binario (ej. archivo BAC). */
async function pedirBlob(ruta: string): Promise<Blob> {
  const token = await obtenerToken();
  const headers: Record<string, string> = {};
  if (token) headers.Authorization = `Bearer ${token}`;

  const respuesta = await fetch(`${API_URL}${ruta}`, { headers });
  if (!respuesta.ok) {
    throw new Error(`HTTP ${respuesta.status}: ${respuesta.statusText}`);
  }
  return respuesta.blob();
}

/**
 * Cliente API tipado. Cada agrupación corresponde a un módulo del backend.
 * Para agregar un endpoint nuevo: extender este objeto y reutilizar `pedir<T>`.
 */
export const cliente = {
  vencimientos: {
    detalle: () => pedir<RespuestaApi<VencimientoDetalle[]>>('/api/vencimientos-detalle'),
    sincronizar: () =>
      pedir<
        RespuestaApi<{
          vendorsSincronizados: number;
          entriesSincronizados: number;
          proveedoresCreados: number;
        }>
      >('/api/sincronizar-pagos', { method: 'POST' }),
  },
  bcVendors: {
    listar: () => pedir<RespuestaApi<VendorBC[]>>('/api/bc-vendors'),
    detalle: (codigo: string) =>
      pedir<RespuestaApi<VendorDetalleBC>>(`/api/bc-vendors/${encodeURIComponent(codigo)}`),
  },
  bcBankAccounts: {
    listar: () => pedir<RespuestaApi<BankAccountBC[]>>('/api/bc-bank-accounts'),
  },
  bcDiariosPagos: {
    listar: () => pedir<RespuestaApi<DiarioPagosBC[]>>('/api/bc-diarios-pagos'),
  },
  propuestasPago: {
    listar: () => pedir<RespuestaApi<PropuestaPagoResumen[]>>('/api/propuestas-pago'),
    detalle: (id: number) =>
      pedir<RespuestaApi<PropuestaPagoDetalle>>(`/api/propuestas-pago/${id}`),
    crear: (data: z.infer<typeof CrearPropuestaPagoSchema>) =>
      pedir<RespuestaApi<{ id: number; sincronizacionCredito: SincronizacionCreditoResultado }>>(
        '/api/propuestas-pago',
        { method: 'POST', body: JSON.stringify(data) },
      ),
    actualizar: (id: number, data: z.infer<typeof ActualizarPropuestaPagoSchema>) =>
      pedir<
        RespuestaApi<{
          actualizado: boolean;
          sincronizacionCredito: SincronizacionCreditoResultado;
        }>
      >(`/api/propuestas-pago/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
    anotarAplicacion: (
      id: number,
      entryNo: number,
      data: { aplicadaADocumento?: string | null; nota?: string | null },
    ) =>
      pedir<RespuestaApi<{ guardado: boolean }>>(
        `/api/propuestas-pago/${id}/lineas/${entryNo}/aplicacion`,
        { method: 'PUT', body: JSON.stringify(data) },
      ),
    quitarAplicacion: (id: number, entryNo: number) =>
      pedir<RespuestaApi<{ quitado: boolean }>>(
        `/api/propuestas-pago/${id}/lineas/${entryNo}/aplicacion`,
        { method: 'DELETE' },
      ),
    refrescarDesdeBC: (id: number) =>
      pedir<RespuestaApi<RefrescoPropuestaResultado>>(`/api/propuestas-pago/${id}/refrescar`, {
        method: 'POST',
      }),
    validar: (id: number) =>
      pedir<RespuestaApi<{ validada: boolean }>>(`/api/propuestas-pago/${id}/validar`, {
        method: 'POST',
      }),
    quitarValidacion: (id: number) =>
      pedir<RespuestaApi<{ validada: boolean }>>(`/api/propuestas-pago/${id}/validar`, {
        method: 'DELETE',
      }),
    reporte: (id: number) =>
      pedir<RespuestaApi<ReportePropuesta>>(`/api/propuestas-pago/${id}/reporte`),
    eliminar: (id: number) =>
      pedir<
        RespuestaApi<{ eliminado: boolean; sincronizacionCredito: SincronizacionCreditoResultado }>
      >(`/api/propuestas-pago/${id}`, { method: 'DELETE' }),
    archivoBanco: (
      id: number,
      params: { cuentaBancoOrigen: string; planCode: string; envioNo: number; fecha: string },
    ) =>
      pedir<RespuestaApi<ArchivoBancoResultado>>(
        `/api/propuestas-pago/${id}/archivo-banco?${new URLSearchParams({
          cuentaBancoOrigen: params.cuentaBancoOrigen,
          planCode: params.planCode,
          envioNo: String(params.envioNo),
          fecha: params.fecha,
        })}`,
      ),
    archivoBancoBN: (
      id: number,
      params: { cuentaBancoOrigen: string; transferenciaNo: number; fecha: string },
    ) =>
      pedir<RespuestaApi<ArchivoBancoBNResultado>>(
        `/api/propuestas-pago/${id}/archivo-banco-bn?${new URLSearchParams({
          cuentaBancoOrigen: params.cuentaBancoOrigen,
          transferenciaNo: String(params.transferenciaNo),
          fecha: params.fecha,
        })}`,
      ),
    archivosBancoGuardados: (id: number) =>
      pedir<RespuestaApi<ArchivoBancoGuardado[]>>(`/api/propuestas-pago/${id}/archivos-banco`),
    validacionArchivoBanco: (id: number, cuentaBancoOrigen: string) =>
      pedir<RespuestaApi<ValidacionArchivoBanco>>(
        `/api/propuestas-pago/${id}/archivo-banco/validacion?${new URLSearchParams({ cuentaBancoOrigen })}`,
      ),
    envioBCVistaPrevia: (
      id: number,
      cuentaBancoOrigen: string,
      diario?: { template: string; batch: string },
      postingDate?: string,
    ) =>
      pedir<RespuestaApi<EnvioBCVistaPrevia>>(
        `/api/propuestas-pago/${id}/envio-bc?${new URLSearchParams({
          cuentaBancoOrigen,
          ...(diario ? { journalTemplate: diario.template, journalBatch: diario.batch } : {}),
          ...(postingDate ? { postingDate } : {}),
        })}`,
      ),
    enviarABC: (
      id: number,
      body: {
        cuentaBancoOrigen: string;
        journalTemplate: string;
        journalBatch: string;
        confirmarDiarioConLineas?: boolean;
        omitirProveedoresConCambios?: boolean;
        postingDate?: string;
      },
    ) =>
      pedir<RespuestaApi<EnvioBCResultado>>(`/api/propuestas-pago/${id}/envio-bc`, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    ultimosEnviosPorCuenta: () =>
      pedir<RespuestaApi<UltimoEnvioBancoPorCuenta[]>>('/api/archivo-banco/ultimos-por-cuenta'),
  },
  beneficiarios: {
    listar: () => pedir<RespuestaApi<Beneficiario[]>>('/api/beneficiarios'),
    colaboradores: (q: string, categoriaId?: number | null) =>
      pedir<RespuestaApi<ColaboradorBusqueda[]>>(
        `/api/beneficiarios/colaboradores?q=${encodeURIComponent(q)}${
          categoriaId ? `&categoriaId=${categoriaId}` : ''
        }`,
      ),
    sincronizarClientes: () =>
      pedir<
        RespuestaApi<{
          enOrigen: number;
          creados: number;
          actualizados: number;
          cedulaRepetida: number;
        }>
      >('/api/beneficiarios/clientes/sincronizar', { method: 'POST' }),
    crear: (data: z.infer<typeof CrearBeneficiarioSchema>) =>
      pedir<RespuestaApi<{ id: number }>>('/api/beneficiarios', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    agregarCuenta: (id: number, data: z.infer<typeof CrearCuentaBeneficiarioSchema>) =>
      pedir<RespuestaApi<{ id: number }>>(`/api/beneficiarios/${id}/cuentas`, {
        method: 'POST',
        body: JSON.stringify(data),
      }),
  },
  detalleSemana: {
    listar: (semanaId: number, categoriaId: number) =>
      pedir<RespuestaApi<DetalleSemanaCategoria>>(
        `/api/detalle-semana?semanaId=${semanaId}&categoriaId=${categoriaId}`,
      ),
    crear: (data: z.infer<typeof CrearDetalleCasoSchema>) =>
      pedir<RespuestaApi<{ id: number }>>('/api/detalle-semana', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    actualizar: (id: number, data: z.infer<typeof ActualizarDetalleCasoSchema>) =>
      pedir<RespuestaApi<{ id: number }>>(`/api/detalle-semana/${id}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      }),
    eliminar: (id: number) =>
      pedir<RespuestaApi<{ eliminado: boolean }>>(`/api/detalle-semana/${id}`, {
        method: 'DELETE',
      }),
    guardarPago: (id: number, data: z.infer<typeof PagoDetalleCasoSchema>) =>
      pedir<RespuestaApi<{ id: number }>>(`/api/detalle-semana/${id}/pago`, {
        method: 'PUT',
        body: JSON.stringify(data),
      }),
  },
  semanas: {
    listar: () => pedir<RespuestaApi<Semana[]>>('/api/semanas'),
    detalle: (id: number) => pedir<RespuestaApi<ResumenSemana>>(`/api/semanas/${id}`),
    crear: (data: z.infer<typeof CrearSemanaSchema>) =>
      pedir<RespuestaApi<{ id: number }>>('/api/semanas', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    actualizarSaldos: (id: number, data: z.infer<typeof ActualizarSaldosSchema>) =>
      pedir<RespuestaApi<{ actualizado: boolean }>>(`/api/semanas/${id}/saldos`, {
        method: 'PUT',
        body: JSON.stringify(data),
      }),
    archivoBanco: (id: number) => pedirBlob(`/api/semanas/${id}/archivo-banco`),
    sugerirBancos: (id: number) =>
      pedir<RespuestaApi<{ asignados: { BAC: number; BN: number }; sinSugerencia: number }>>(
        `/api/semanas/${id}/sugerir-bancos`,
        { method: 'POST', body: '{}' },
      ),
    asegurarActual: () =>
      pedir<RespuestaApi<{ id: number; creada: boolean }>>('/api/semanas/asegurar-actual', {
        method: 'POST',
        body: '{}',
      }),
    traerPendientes: (id: number) =>
      pedir<RespuestaApi<{ copiados: number; semanaOrigenId: number | null }>>(
        `/api/semanas/${id}/traer-pendientes`,
        { method: 'POST', body: '{}' },
      ),
    sincronizarConceptos: (id: number) =>
      pedir<RespuestaApi<{ agregados: number }>>(`/api/semanas/${id}/sincronizar-conceptos`, {
        method: 'POST',
        body: '{}',
      }),
  },
  obligaciones: {
    listar: (params?: { conceptoId?: number; pendientes?: boolean }) => {
      const q = new URLSearchParams();
      if (params?.conceptoId) q.set('conceptoId', String(params.conceptoId));
      if (params?.pendientes === false) q.set('pendientes', 'false');
      return pedir<RespuestaApi<(Obligacion & { conceptoNombre: string; moneda: string })[]>>(
        `/api/obligaciones${q.toString() ? `?${q}` : ''}`,
      );
    },
    crear: (data: z.infer<typeof CrearObligacionSchema>) =>
      pedir<RespuestaApi<{ id: number }>>('/api/obligaciones', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    actualizar: (id: number, data: z.infer<typeof ActualizarObligacionSchema>) =>
      pedir<RespuestaApi<{ actualizado: boolean }>>(`/api/obligaciones/${id}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      }),
    eliminar: (id: number) =>
      pedir<RespuestaApi<{ eliminado: boolean }>>(`/api/obligaciones/${id}`, { method: 'DELETE' }),
    generarSiguiente: (conceptoId: number) =>
      pedir<
        RespuestaApi<{
          id: number;
          periodoDescripcion: string;
          periodoFecha: string;
          monto: number | null;
          esMontFijo: boolean;
        }>
      >(`/api/conceptos/${conceptoId}/obligaciones/siguiente`, { method: 'POST', body: '{}' }),
  },
  pagosAdicionales: {
    crear: (semanaId: number, data: z.infer<typeof CrearPagoAdicionalSchema>) =>
      pedir<RespuestaApi<{ pagoId: number; conceptoId: number }>>(
        `/api/semanas/${semanaId}/pagos-adicionales`,
        { method: 'POST', body: JSON.stringify(data) },
      ),
  },
  pagos: {
    actualizar: (id: number, data: z.infer<typeof ActualizarPagoSchema>) =>
      pedir<RespuestaApi<{ actualizado: boolean }>>(`/api/pagos/${id}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      }),
    crear: (data: z.infer<typeof CrearPagoSchema>) =>
      pedir<RespuestaApi<{ id: number }>>('/api/pagos', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
  },
  pagosCredito: {
    /** Histórico plano de pagos a proveedores de crédito (detalle del resumen semanal). */
    listar: (filtros: FiltrosPagosCredito = {}) => {
      const q = new URLSearchParams();
      for (const [clave, valor] of Object.entries(filtros)) {
        if (valor !== null && valor !== undefined && valor !== '') q.set(clave, String(valor));
      }
      return pedir<RespuestaApi<HistoricoPagosCredito>>(
        `/api/pagos-credito${q.toString() ? `?${q}` : ''}`,
      );
    },
  },
  conceptos: {
    listar: (soloActivos = true) =>
      pedir<RespuestaApi<Concepto[]>>(`/api/conceptos${soloActivos ? '' : '?activos=false'}`),
    crear: (data: z.infer<typeof CrearConceptoSchema>) =>
      pedir<RespuestaApi<{ id: number }>>('/api/conceptos', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    eliminar: (data: z.input<typeof EliminarConceptosSchema>) =>
      pedir<RespuestaApi<EliminarConceptosResultado>>('/api/conceptos/eliminar', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    actualizar: (id: number, data: z.infer<typeof ActualizarConceptoSchema>) =>
      pedir<RespuestaApi<{ actualizado: boolean }>>(`/api/conceptos/${id}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      }),
    sugerenciasProveedor: () =>
      pedir<RespuestaApi<ConceptoSinVincular[]>>('/api/conceptos/sugerencias-proveedor'),
    vincularProveedor: (id: number, data: z.infer<typeof VincularProveedorConceptoSchema>) =>
      pedir<RespuestaApi<{ proveedorId: number }>>(`/api/conceptos/${id}/vincular-proveedor`, {
        method: 'POST',
        body: JSON.stringify(data),
      }),
  },
  yo: {
    obtener: () => pedir<RespuestaApi<UsuarioActual>>('/api/yo'),
  },
  ordenesCompraBC: {
    porProveedor: (vendorCode: string) =>
      pedir<RespuestaApi<OrdenCompraBC[]>>(
        `/api/bc-ordenes-compra?vendorCode=${encodeURIComponent(vendorCode)}`,
      ),
    resumen: () =>
      pedir<RespuestaApi<{ vendorCode: string; cantidad: number }[]>>('/api/bc-ordenes-resumen'),
    detalle: (numero: string) =>
      pedir<RespuestaApi<OrdenCompraDetalleBC>>(
        `/api/bc-ordenes-compra/${encodeURIComponent(numero)}`,
      ),
  },
  solicitudesContado: {
    listar: () => pedir<RespuestaApi<SolicitudContado[]>>('/api/solicitudes-contado'),
    crear: (data: z.infer<typeof CrearSolicitudContadoSchema>) =>
      pedir<RespuestaApi<{ id: number; aviso: string | null }>>('/api/solicitudes-contado', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    actualizar: (id: number, data: z.infer<typeof ActualizarSolicitudContadoSchema>) =>
      pedir<RespuestaApi<{ actualizada: boolean }>>(`/api/solicitudes-contado/${id}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      }),
    eliminar: (id: number) =>
      pedir<RespuestaApi<{ eliminada: boolean }>>(`/api/solicitudes-contado/${id}`, {
        method: 'DELETE',
      }),
    aprobar: (id: number, montoAprobado?: number, documentosAprobados?: string[]) =>
      pedir<RespuestaApi<{ aprobada: boolean; divididaEn: number | null; saldoEn: number | null }>>(
        `/api/solicitudes-contado/${id}/aprobar`,
        {
          method: 'POST',
          body: JSON.stringify({
            ...(montoAprobado === undefined ? {} : { montoAprobado }),
            ...(documentosAprobados === undefined ? {} : { documentosAprobados }),
          }),
        },
      ),
    rechazar: (id: number, motivo: string | null) =>
      pedir<RespuestaApi<{ rechazada: boolean }>>(`/api/solicitudes-contado/${id}/rechazar`, {
        method: 'POST',
        body: JSON.stringify({ motivo }),
      }),
    reabrir: (id: number) =>
      pedir<RespuestaApi<{ reabierta: boolean }>>(`/api/solicitudes-contado/${id}/reabrir`, {
        method: 'POST',
        body: '{}',
      }),
    liberarSaldo: (id: number) =>
      pedir<RespuestaApi<{ id: number; saldo: number }>>(
        `/api/solicitudes-contado/${id}/liberar-saldo`,
        { method: 'POST', body: '{}' },
      ),
  },
  listasContado: {
    listar: () => pedir<RespuestaApi<ListaContado[]>>('/api/listas-contado'),
    detalle: (id: number) => pedir<RespuestaApi<ListaContadoDetalle>>(`/api/listas-contado/${id}`),
    cerrar: (id: number) =>
      pedir<RespuestaApi<{ cerrada: boolean }>>(`/api/listas-contado/${id}/cerrar`, {
        method: 'POST',
        body: '{}',
      }),
    reabrir: (id: number) =>
      pedir<RespuestaApi<{ reabierta: boolean }>>(`/api/listas-contado/${id}/reabrir`, {
        method: 'POST',
        body: '{}',
      }),
    marcarPagada: (id: number, referencia: string | null, fechaPago?: string) =>
      pedir<RespuestaApi<{ archivos: unknown[] }>>(`/api/listas-contado/${id}/marcar-pagada`, {
        method: 'POST',
        body: JSON.stringify({ referencia, ...(fechaPago ? { fechaPago } : {}) }),
      }),
    cambiarFechaPago: (idArchivo: number, fecha: string) =>
      pedir<RespuestaApi<{ fecha: string }>>(`/api/listas-contado/archivos/${idArchivo}/fecha`, {
        method: 'PUT',
        body: JSON.stringify({ fecha }),
      }),
    renombrar: (id: number, nombre: string) =>
      pedir<RespuestaApi<{ renombrada: boolean }>>(`/api/listas-contado/${id}`, {
        method: 'PUT',
        body: JSON.stringify({ nombre }),
      }),
    eliminar: (id: number) =>
      pedir<RespuestaApi<{ eliminada: boolean }>>(`/api/listas-contado/${id}`, {
        method: 'DELETE',
      }),
    vistaPrevia: (
      id: number,
      cuentaBancoOrigen: string,
      ids: number[],
      cuentasElegidas: Record<string, number> = {},
    ) =>
      pedir<RespuestaApi<VistaPagoContado>>(
        `/api/listas-contado/${id}/vista-previa?cuentaBancoOrigen=${encodeURIComponent(cuentaBancoOrigen)}&ids=${ids.join(',')}&cuentas=${encodeURIComponent(JSON.stringify(cuentasElegidas))}`,
      ),
    generar: (id: number, data: z.infer<typeof GenerarArchivoContadoSchema>) =>
      pedir<RespuestaApi<ResultadoGenerarContado>>(`/api/listas-contado/${id}/archivo-banco`, {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    pagarAMano: (id: number, data: z.infer<typeof PagarContadoAManoSchema>) =>
      pedir<RespuestaApi<ArchivoPagoContado>>(`/api/listas-contado/${id}/pagar-a-mano`, {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    contenido: (idArchivo: number) =>
      pedir<RespuestaApi<ArchivoContado[]>>(`/api/listas-contado/archivos/${idArchivo}/contenido`),
    marcarPagado: (idArchivo: number) =>
      pedir<RespuestaApi<{ pagado: boolean }>>(`/api/listas-contado/archivos/${idArchivo}/pagado`, {
        method: 'POST',
        body: '{}',
      }),
    deshacer: (idArchivo: number) =>
      pedir<RespuestaApi<{ deshecho: boolean }>>(
        `/api/listas-contado/archivos/${idArchivo}/deshacer`,
        { method: 'POST', body: '{}' },
      ),
  },
  proveedoresPactados: {
    listar: () => pedir<RespuestaApi<ProveedoresPactadosResultado>>('/api/proveedores-pactados'),
    marcar: (vendorCode: string, motivo: string | null) =>
      pedir<RespuestaApi<{ marcado: boolean }>>(
        `/api/proveedores-pactados/${encodeURIComponent(vendorCode)}`,
        { method: 'PUT', body: JSON.stringify({ motivo }) },
      ),
    desmarcar: (vendorCode: string) =>
      pedir<RespuestaApi<{ marcado: boolean }>>(
        `/api/proveedores-pactados/${encodeURIComponent(vendorCode)}`,
        { method: 'DELETE' },
      ),
  },
  proveedores: {
    listar: () => pedir<RespuestaApi<Proveedor[]>>('/api/proveedores'),
    /** Proveedores de BC con movimiento reciente que faltan en la app (simular = solo vista previa). */
    importarDeBC: (simular: boolean, meses = 6) =>
      pedir<RespuestaApi<ResultadoImportacionBC>>(
        `/api/proveedores-importar-bc?simular=${simular}&meses=${meses}`,
        { method: 'POST' },
      ),
    crear: (data: z.infer<typeof CrearProveedorSchema>) =>
      pedir<RespuestaApi<{ id: number }>>('/api/proveedores', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    actualizar: (id: number, data: z.infer<typeof ActualizarProveedorSchema>) =>
      pedir<RespuestaApi<{ actualizado: boolean }>>(`/api/proveedores/${id}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      }),
  },
  proyeccion: {
    obtener: (params: { semanas: 6 | 8; meses?: number }) => {
      const q = new URLSearchParams({ semanas: String(params.semanas) });
      if (params.meses) q.set('meses', String(params.meses));
      return pedir<RespuestaApi<ReporteProyeccion>>(`/api/proyeccion?${q}`);
    },
  },
  categorias: {
    listar: () => pedir<RespuestaApi<Categoria[]>>('/api/categorias'),
    crear: (nombre: string, padreId?: number | null) =>
      pedir<RespuestaApi<{ id: number }>>('/api/categorias', {
        method: 'POST',
        body: JSON.stringify({ nombre, padreId: padreId ?? null }),
      }),
    actualizar: (id: number, nombre: string) =>
      pedir<RespuestaApi<{ actualizado: boolean }>>(`/api/categorias/${id}`, {
        method: 'PUT',
        body: JSON.stringify({ nombre }),
      }),
    eliminar: (id: number) =>
      pedir<RespuestaApi<{ eliminado: boolean }>>(`/api/categorias/${id}`, { method: 'DELETE' }),
  },
};

/** Información del usuario activo (modo dev o MSAL real). */
export interface UsuarioActivo {
  name: string;
  username: string;
  iniciales: string;
}

/**
 * Obtiene el usuario activo. En modo dev devuelve `usuarioDev`.
 * En modo MSAL devuelve la primera cuenta o `null` si no hay sesión.
 */
export function obtenerUsuarioActual(): UsuarioActivo | null {
  if (modoDev) return { ...usuarioDev };

  const cuenta = msalInstance?.getAllAccounts()[0];
  if (!cuenta) return null;

  const nombre = cuenta.name ?? cuenta.username;
  return {
    name: nombre,
    username: cuenta.username,
    iniciales: obtenerIniciales(nombre),
  };
}
